// src/encounter/ambient/ambient-window.tsx
//
// `ambientWindow` (`?x=ambient-window`) · the ambient session as a floating window, as Antonin's catalog draws it (scrips-signal-ds
// feat/signal @62cb3650, components/AmbientScribe: "a floating window over the chart (no backdrop, the encounter stays readable),
// dragged by its header (or its grip with the arrow keys)"; it opens bottom-centre over the rail; expanded 960 x 640, compact
// 420 x 520, collapsed to one 52 bar).
//
// The window only frames: its body is today's ambient panel (`AmbientScribePanel`), the same element with the same props, so the
// transcript, the suggestions, the consent gate and every Accept are unchanged; nothing enters the note until a clinician accepts
// it. Closed or collapsed, the panel stays MOUNTED (hidden), so a running session is never torn down by closing the window.
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@scripsteam/scrips-signal-ds';
import { GripHorizontal, Maximize2, Minimize2, Minus, X } from 'lucide-react';
import { AMBIENT_WINDOW_WORDS, ambientWindowClamp, ambientWindowStart, type AmbientWindowSize } from './ambient-window-words';

export function AmbientWindow({
  open,
  onClose,
  status,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** The session's status words for the header ("Listening · 03:12"); the panel owns them. */
  status?: ReactNode;
  children: ReactNode;
}) {
  const [size, setSize] = useState<AmbientWindowSize>('expanded');
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // The viewport, kept current: a resize re-limits the window (#904 review).
  const [vp, setVp] = useState(() => ({
    vw: typeof window === 'undefined' ? 1440 : window.innerWidth,
    vh: typeof window === 'undefined' ? 900 : window.innerHeight,
  }));
  useEffect(() => {
    const onResize = () => setVp({ vw: window.innerWidth, vh: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { vw, vh } = vp;
  const start = ambientWindowStart(size, vw, vh);
  // The whole window stays on screen, after a drag, the arrow keys, a size change or a resize.
  const box = { w: start.w, h: size === 'collapsed' ? 52 : start.h };
  const at = ambientWindowClamp(pos ?? start, box, vw, vh);
  // The running drag's listeners, so a close, an unmount or a pointercancel removes them (#904 review).
  const endDrag = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!open) endDrag.current?.();
  }, [open]);
  useEffect(() => () => endDrag.current?.(), []);
  const drag = (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    endDrag.current?.();
    const sx = e.clientX - at.x;
    const sy = e.clientY - at.y;
    const move = (ev: globalThis.PointerEvent) => setPos(ambientWindowClamp({ x: ev.clientX - sx, y: ev.clientY - sy }, box, vw, vh));
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      endDrag.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    endDrag.current = end;
  };
  const key = (e: KeyboardEvent) => {
    const d = e.shiftKey ? 48 : 16;
    const m: Record<string, [number, number]> = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] };
    if (!m[e.key]) return;
    e.preventDefault();
    setPos(ambientWindowClamp({ x: at.x + m[e.key][0], y: at.y + m[e.key][1] }, box, vw, vh));
  };
  const icon = (label: string, Glyph: typeof X, onClick: () => void) => (
    <Button key={label} variant="tertiary" size="sm" className="text-text-secondary" aria-label={label} title={label} icon={<Glyph className="size-4" aria-hidden />} onClick={onClick} />
  );
  const win = (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={AMBIENT_WINDOW_WORDS.title}
      data-slot="ambient-window"
      data-size={size}
      hidden={!open}
      // RUNTIME: the window's place and size follow the viewport and the clinician's drag.
      style={{ '--aw-x': `${at.x}px`, '--aw-y': `${at.y}px`, '--aw-w': `${start.w}px`, '--aw-h': `${start.h}px` } as CSSProperties}
      className={`fixed top-[var(--aw-y)] left-[var(--aw-x)] z-50 flex w-[var(--aw-w)] flex-col overflow-hidden rounded-xl bg-surface-card shadow-lg${size === 'collapsed' ? '' : ' h-[var(--aw-h)]'}`}
    >
      <div
        data-slot="ambient-window-header"
        className="flex flex-none cursor-grab touch-none items-center gap-2 border-b border-border-default px-4 py-3 select-none"
        onPointerDown={drag}
      >
        <button type="button" className="grid size-6 flex-none place-items-center rounded-sm text-text-muted" aria-label={AMBIENT_WINDOW_WORDS.move} onKeyDown={key}>
          <GripHorizontal className="size-4" aria-hidden />
        </button>
        <span className="text-sm font-medium text-text-primary">{AMBIENT_WINDOW_WORDS.title}</span>
        {status ? <span className="text-xs text-text-secondary tabular-nums">{status}</span> : null}
        <span className="ml-auto flex flex-none items-center gap-1">
          {size === 'expanded' ? icon(AMBIENT_WINDOW_WORDS.compact, Minimize2, () => setSize('compact')) : icon(AMBIENT_WINDOW_WORDS.expand, Maximize2, () => setSize('expanded'))}
          {size !== 'collapsed' && icon(AMBIENT_WINDOW_WORDS.collapse, Minus, () => setSize('collapsed'))}
          {icon(AMBIENT_WINDOW_WORDS.close, X, onClose)}
        </span>
      </div>
      {/* The panel stays mounted when collapsed: only hidden. */}
      <div data-slot="ambient-window-body" hidden={size === 'collapsed'} className="min-h-0 flex-1 overflow-y-auto p-4">
        {children}
      </div>
    </div>
  );
  return typeof document === 'undefined' ? win : createPortal(win, document.body);
}
